import { useCallback } from 'react';
import { CoreMessages, IntlMessageId } from 'locale';
import { useIntlFormat } from 'utils/intl/useIntlFormat';
import { TenantSubFeatureModel } from '@utmost.co/enterprise-models';
import { FeaturesNames } from 'src/definitions/custom-models';
import { useTenantConfig } from './useTenantConfig';

export interface FeatureLabel {
  featureId?: FeaturesNames;
  subFeatureId?: 'work-request' | 'job-posting' | 'job-application' | 'contract';
  messageId: IntlMessageId;
  useMessageIdFirst?: boolean;
}

/**
 * @returns Function to allow label to be extracted at the feature
 * or sub-feature level. Defaults to formatted message id.
 */
export const useFeatureLabel = () => {
  const format = useIntlFormat();
  const uiConfig = useTenantConfig()?.features;

  const getLabel = useCallback(
    (item: FeatureLabel) => {
      // force using the translation first
      if (item.messageId && item.useMessageIdFirst) {
        return format(item.messageId as CoreMessages);
      }

      // Check sub feature for label.
      let label: string | undefined;
      if (item.featureId && item.subFeatureId) {
        label = uiConfig?.[item.featureId]?.orderedFeatures?.find?.(
          (orderedFeature: TenantSubFeatureModel) => orderedFeature.name === item.subFeatureId && orderedFeature.enabled
        )?.label;
      }

      // Check main feature for label.
      if (!label && item.featureId && item.featureId !== 'dashboards') {
        label = uiConfig?.[item.featureId]?.label;
      }
      if (label) {
        return label;
      }

      return format(item.messageId as CoreMessages);
    },
    [format, uiConfig]
  );

  return { getLabel };
};
